import { Field, Formik, type FormikConfig, type FormikHandlers, type FormikProps } from 'formik'
import React, { type ReactNode, type RefObject } from 'react'

import { Button, Checkbox, Loader, RadioButton } from '@fourthwall/components'
import type { Money } from '@fourthwall/services/dist/models/common'

import type { PaymentMethodItem } from '../../PaymentMethods.types'
import { ChargeLimitFormGroup } from '../../components/ChargeLimitFormGroup/ChargeLimitFormGroup'
import { PaymentMethodsList } from '../../components/PaymentMethodsList/PaymentMethodsList'
import type {
  ChildrenProps,
  FormType,
  FormValues,
  PaymentMethodType,
  RenderSubmitButtonProps,
} from './AddPaymentMethod.types'
import * as S from './AddPaymentMethodView.styled'
import payPalIcon from './assets/Paypal.svg'
import { StripeForm, type StripeFormProps } from './components/StripeForm/StripeForm'
import { PAYPAL_CONTAINER_ID } from './consts'

export type AddPaymentMethodViewProps = {
  formikRef: RefObject<FormikProps<FormValues>>
  submitLabel: string
  paymentMethodsProps?: PaymentMethodItem[]
  fields: StripeFormProps['fields']
  initialValues: FormValues
  validationSchema: FormikConfig<FormValues>['validationSchema']
  shouldRenderChargeLimit: boolean
  shouldRenderIsPaymentMethodDefaultCheckbox: boolean
  currentMonthSpendingValue?: Money
  isSubmitting: boolean
  isLoading: boolean
  onSubmit: (values: FormValues) => void
  onPaymentMethodTypeChange: (formType: FormType, paymentMethodType: PaymentMethodType) => void
  children: (childrenProps: ChildrenProps) => ReactNode
}

export const AddPaymentMethodView = ({
  formikRef,
  submitLabel,
  fields,
  paymentMethodsProps,
  initialValues,
  validationSchema,
  shouldRenderChargeLimit,
  shouldRenderIsPaymentMethodDefaultCheckbox,
  currentMonthSpendingValue,
  isSubmitting,
  isLoading,
  onSubmit,
  onPaymentMethodTypeChange,
  children,
}: AddPaymentMethodViewProps) => {
  const renderIsPaymentMethodDefaultCheckbox = () => {
    if (!shouldRenderIsPaymentMethodDefaultCheckbox) {
      return null
    }

    return (
      <S.CheckboxWrapper>
        <Field
          component={Checkbox}
          name="isPaymentMethodDefault"
          label="Set as default payment method"
        />
      </S.CheckboxWrapper>
    )
  }

  const renderNewPaymentForm = ({ formType }: FormValues) => {
    return (
      <>
        <S.PaymentMethodTypeItem>
          <Field
            component={RadioButton}
            name="paymentMethodType"
            label="Credit or debit card"
            checkedValue="STRIPE"
            onValueChange={(value: PaymentMethodType) => onPaymentMethodTypeChange(formType, value)}
          />
          <S.StripeFormWrapper>
            <StripeForm fields={fields} />
          </S.StripeFormWrapper>
        </S.PaymentMethodTypeItem>
        <S.PaymentMethodTypeItem>
          <Field
            component={RadioButton}
            name="paymentMethodType"
            label={<S.PayPalIcon src={payPalIcon} alt="PayPal" />}
            checkedValue="PAYPAL"
            onValueChange={(value: PaymentMethodType) => onPaymentMethodTypeChange(formType, value)}
          />
        </S.PaymentMethodTypeItem>
        {renderIsPaymentMethodDefaultCheckbox()}
      </>
    )
  }

  const renderNewPayment = (formValues: FormValues) => {
    if (isLoading || !paymentMethodsProps || paymentMethodsProps.length) {
      return null
    }

    return renderNewPaymentForm(formValues)
  }

  const renderPaymentChoice = (values: FormValues) => {
    const { formType, paymentMethodType } = values
    if (isLoading || !paymentMethodsProps?.length) {
      return null
    }

    return (
      <>
        <S.RadioWrapper>
          <Field
            component={RadioButton}
            name="formType"
            label="Add a payment method from another Fourthwall site you manage"
            checkedValue="EXISTING"
            onValueChange={(value: FormType) => onPaymentMethodTypeChange(value, paymentMethodType)}
          />
          <S.RadioContent isHidden={formType !== 'EXISTING'}>
            <PaymentMethodsList paymentMethods={paymentMethodsProps} selectable />
            {renderIsPaymentMethodDefaultCheckbox()}
          </S.RadioContent>
        </S.RadioWrapper>

        <S.RadioWrapper>
          <Field
            component={RadioButton}
            name="formType"
            label="Add a new payment method"
            checkedValue="NEW"
            onValueChange={(value: FormType) => onPaymentMethodTypeChange(value, paymentMethodType)}
          />
          <S.RadioContent isHidden={formType !== 'NEW'}>
            {renderNewPaymentForm(values)}
          </S.RadioContent>
        </S.RadioWrapper>
      </>
    )
  }

  const renderTransactionLimitFormGroup = ({ setChargeLimit }: FormValues) => {
    if (isLoading || !shouldRenderChargeLimit) {
      return null
    }

    return (
      <S.CheckboxWrapper>
        <ChargeLimitFormGroup
          isEnabled={setChargeLimit}
          currentMonthSpending={currentMonthSpendingValue}
        />
      </S.CheckboxWrapper>
    )
  }

  const renderForm = (values: FormValues) => {
    return (
      <>
        {renderPaymentChoice(values)}

        {renderNewPayment(values)}

        {renderTransactionLimitFormGroup(values)}
      </>
    )
  }

  const renderSubmitButton = (
    { paymentMethodType, formType }: FormValues,
    handleSubmit: FormikHandlers['handleSubmit'],
    options: RenderSubmitButtonProps,
  ) => {
    return (
      <>
        <S.PayPalButtonContainer visible={paymentMethodType === 'PAYPAL' && formType === 'NEW'}>
          {paymentMethodType === 'PAYPAL' && formType === 'NEW' && isSubmitting && (
            <S.PayPalButtonOverlay>
              <Loader size="large" />
            </S.PayPalButtonOverlay>
          )}
          <div id={PAYPAL_CONTAINER_ID} />
        </S.PayPalButtonContainer>
        {(paymentMethodType === 'STRIPE' || formType === 'EXISTING') && (
          <Button
            label={submitLabel}
            appearance="primary"
            type="button"
            onClick={() => handleSubmit()}
            fullWidth={options.fullWidth}
            loading={isSubmitting || options.isLoading}
            disabled={isLoading}
          />
        )}
      </>
    )
  }

  return (
    <Formik<FormValues>
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({ values, handleSubmit }) =>
        children({
          renderForm: () => renderForm(values),
          renderSubmitButton: (options = {}) => renderSubmitButton(values, handleSubmit, options),
          isLoading,
        })
      }
    </Formik>
  )
}
